
import React, { createContext, useContext, useState, useEffect, useReducer, useCallback } from 'react';
import { verifyQuestWithTimer } from '../api/dataService';
import { generatePhaseSequence, rehydrateTimerStateWithFreshPhases } from '../utils/timerUtils';
import {
    buildWorkoutPerformanceDraft,
    mergeWorkoutPerformanceDraft,
    resolvePreferredWeightUnit,
    updateWorkoutPerformanceSet
} from '../utils/workoutPerformanceUtils';

const TimerContext = createContext();

const initialState = {
    quest: null,
    phases: [],
    currentPhaseIndex: 0,
    timeRemaining: 0,
    status: 'idle', // idle, running, paused, completed
    sessionLogs: [],
    workoutPerformance: null,
    startTime: null,
};

function timerReducer(state, action) {
    switch (action.type) {
        case 'START_TIMER':
            // This action now completely replaces the old state.
            return {
                ...initialState,
                quest: action.payload.quest,
                phases: action.payload.phases,
                workoutPerformance: action.payload.workoutPerformance,
                timeRemaining: action.payload.phases[0]?.duration || 0,
                status: 'running',
                startTime: Date.now(),
            };
        case 'RESTORE_STATE':
            // This action also replaces the old state.
            return action.payload;
        case 'TICK':
            return { ...state, timeRemaining: state.timeRemaining - 1 };
        case 'PAUSE_TIMER':
            return { ...state, status: 'paused' };
        case 'RESUME_TIMER':
            return { ...state, status: 'running' };
        case 'NEXT_PHASE':
            return {
                ...state,
                currentPhaseIndex: state.currentPhaseIndex + 1,
                timeRemaining: state.phases[state.currentPhaseIndex + 1]?.duration || 0,
            };
        case 'COMPLETE_TIMER':
            return { ...state, status: 'completed' };
        case 'RESET_TIMER':
            return initialState;
        case 'LOG_PHASE':
            return { ...state, sessionLogs: [...state.sessionLogs, action.payload] };
        case 'UPDATE_WORKOUT_PERFORMANCE_SET':
            return {
                ...state,
                workoutPerformance: updateWorkoutPerformanceSet(
                    state.workoutPerformance,
                    action.payload.exerciseIndex,
                    action.payload.setNumber,
                    action.payload.patch
                )
            };
        default:
            return state;
    }
}

export const TimerProvider = ({ children, user }) => {
    const [state, dispatch] = useReducer(timerReducer, initialState);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const verificationListeners = React.useRef([]);

    const getStorageKey = useCallback(() => {
        if (!user?.id || !state.quest?._id) return null;
        return `forza-timer-session-${user.id}-${state.quest._id}`;
    }, [user?.id, state.quest?._id]);

    // Timer countdown effect
    useEffect(() => {
        if (state.status !== 'running') return;

        const interval = setInterval(() => {
            dispatch({ type: 'TICK' });
        }, 1000);

        return () => clearInterval(interval);
    }, [state.status]);

    // State persistence to localStorage
    useEffect(() => {
        const storageKey = getStorageKey();
        if (storageKey && state.status !== 'idle') {
            localStorage.setItem(storageKey, JSON.stringify(state));
        }
    }, [state, getStorageKey]);

    // Page visibility effect
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && state.status === 'running') {
                dispatch({ type: 'PAUSE_TIMER' });
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [state.status]);

    // Phase transition and completion logic
    useEffect(() => {
        if (state.timeRemaining > 0 || state.status !== 'running') return;

        const currentPhase = state.phases[state.currentPhaseIndex];
        dispatch({ type: 'LOG_PHASE', payload: { ...currentPhase, actualDuration: currentPhase.duration, skipped: false, timestamp: Date.now() } });

        if (state.currentPhaseIndex < state.phases.length - 1) {
            dispatch({ type: 'NEXT_PHASE' });
        } else {
            dispatch({ type: 'COMPLETE_TIMER' });
        }
    }, [state.timeRemaining, state.status, state.currentPhaseIndex, state.phases]);

    // API submission on completion
    useEffect(() => {
        if (state.status !== 'completed' || isSubmitting) return;

        const submitVerification = async () => {
            setIsSubmitting(true);
            setError(null);
            let responseData = null;
            const sessionData = {
                startTime: state.startTime,
                endTime: Date.now(),
                totalDuration: Math.floor((Date.now() - state.startTime) / 1000),
                phaseLogs: state.sessionLogs,
                skippedPhases: state.sessionLogs.filter(p => p.skipped).length,
                completedPhases: state.sessionLogs.filter(p => !p.skipped).length,
                workoutPerformance: state.workoutPerformance,
            };

            try {
                const response = await verifyQuestWithTimer(state.quest._id, sessionData);
                responseData = response?.data || null;
                const storageKey = getStorageKey();
                if (storageKey) {
                    localStorage.removeItem(storageKey);
                }
                dispatch({ type: 'RESET_TIMER' });
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to verify quest. Please try again.');
            } finally {
                setIsSubmitting(false);
                if (responseData) {
                    verificationListeners.current.forEach((listener) => {
                        try {
                            listener(responseData);
                        } catch (notifyErr) {
                            console.error('[TimerContext] Verification listener error', notifyErr);
                        }
                    });
                }
            }
        };

        submitVerification();
    }, [state.status, state.quest, state.startTime, state.sessionLogs, state.workoutPerformance, isSubmitting, getStorageKey]);

    const startTimer = useCallback((quest) => {
        // If the timer is already active for this exact quest, do nothing.
        if (state.quest?._id === quest._id) {
            return;
        }

        // If we are here, it means we are starting a timer for a new and different quest.
        const storageKey = user?.id && quest?._id ? `forza-timer-session-${user.id}-${quest._id}` : null;
        const savedState = storageKey ? localStorage.getItem(storageKey) : null;

        if (savedState) {
            const parsedState = JSON.parse(savedState);
            const rehydratedState = rehydrateTimerStateWithFreshPhases(parsedState, quest);
            rehydratedState.workoutPerformance = mergeWorkoutPerformanceDraft(
                rehydratedState.workoutPerformance,
                quest,
                resolvePreferredWeightUnit(user)
            );
            // When restoring, if it was running, set it to paused so the user must explicitly resume.
            if (rehydratedState.status === 'running') {
                rehydratedState.status = 'paused';
            }
            dispatch({ type: 'RESTORE_STATE', payload: rehydratedState });
        } else {
            const phases = generatePhaseSequence(quest);
            if (phases.length > 0) {
                dispatch({
                    type: 'START_TIMER',
                    payload: {
                        quest,
                        phases,
                        workoutPerformance: buildWorkoutPerformanceDraft(quest, resolvePreferredWeightUnit(user))
                    }
                });
            }
        }
    }, [user, state.quest?._id]);

    const pauseTimer = () => dispatch({ type: 'PAUSE_TIMER' });
    const resumeTimer = () => dispatch({ type: 'RESUME_TIMER' });

    const skipPhase = () => {
        const currentPhase = state.phases[state.currentPhaseIndex];
        const timeElapsed = currentPhase.duration - state.timeRemaining;

        if (timeElapsed >= currentPhase.duration / 2) {
            dispatch({ type: 'LOG_PHASE', payload: { ...currentPhase, actualDuration: timeElapsed, skipped: true, timestamp: Date.now() } });
            if (state.currentPhaseIndex < state.phases.length - 1) {
                dispatch({ type: 'NEXT_PHASE' });
            } else {
                dispatch({ type: 'COMPLETE_TIMER' });
            }
        }
    };

    const resetTimer = () => {
        const storageKey = getStorageKey();
        if (storageKey) {
            localStorage.removeItem(storageKey);
        }
        dispatch({ type: 'RESET_TIMER' });
    };

    const updateWorkoutPerformance = useCallback((exerciseIndex, setNumber, patch) => {
        dispatch({
            type: 'UPDATE_WORKOUT_PERFORMANCE_SET',
            payload: { exerciseIndex, setNumber, patch }
        });
    }, []);

    const addVerificationListener = useCallback((listener) => {
        if (typeof listener !== 'function') return () => {};
        verificationListeners.current = [...verificationListeners.current, listener];
        return () => {
            verificationListeners.current = verificationListeners.current.filter((fn) => fn !== listener);
        };
    }, []);

    const value = {
        ...state,
        error,
        isSubmitting,
        startTimer,
        pauseTimer,
        resumeTimer,
        skipPhase,
        resetTimer,
        updateWorkoutPerformance,
        addVerificationListener,
    };

    return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export const useTimer = () => {
    const context = useContext(TimerContext);
    if (context === undefined) {
        throw new Error('useTimer must be used within a TimerProvider');
    }
    return context;
};
